<template>
  <div class="sla-dashboard">
    <!-- 页面头部：标题 + 筛选项 -->
    <section class="page-head">
      <div class="title-wrap">
        <div class="page-icon">◴</div>
        <div>
          <div class="page-title">SLA Diário | SLA时效看板</div>
        </div>
        <div class="update">
          <b>{{ $t('collectionCenter.hubVal2') }}</b>{{ updateTime }}
        </div>
      </div>
      <div class="filters">
        <div class="filter">
          <label>
            <span class="label-cn">应交付周</span>
            <span class="label-pt">Semana de entrega</span>
          </label>
          <el-select
            v-model="filters.week"
            :placeholder="$t('GlobalSetObj.pleaseChoose')"
            class="filter-select"
            clearable
            size="small"
            @change="onWeekChange"
          >
            <el-option
              v-for="item in weekOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
        <div class="filter">
          <label>
            <span class="label-cn">客户名称</span>
            <span class="label-pt">Nome do cliente</span>
          </label>
          <el-select
            v-model="filters.customerId"
            :placeholder="$t('collectionCenter.pleaseChoose')"
            :remote-method="getCustomerPage"
            class="filter-select"
            clearable
            filterable
            remote
            reserve-keyword
            size="small"
            style="width: 100%"
            @clear="getCustomerPage('')"
          >
            <el-option
              v-for="item in customerNameList"
              :key="item.id"
              :label="item.username"
              :value="item.id"
            />
          </el-select>
        </div>
        <div class="filter">
          <label>
            <span class="label-cn">商户名称</span>
            <span class="label-pt">Nome do comerciante</span>
          </label>
          <el-select
            v-model="filters.merchantCode"
            :placeholder="$t('collectionCenter.pleaseChoose')"
            :remote-method="getMerchantList"
            class="filter-select"
            clearable
            filterable
            remote
            reserve-keyword
            size="small"
            style="width: 100%"
            @clear="getMerchantList('')"
          >
            <el-option
              v-for="item in merchantList"
              :key="item.merchantCode"
              :label="item.merchantName"
              :value="item.merchantCode"
            />
          </el-select>
        </div>
        <div class="filter filter--date">
          <label>
            <span class="label-cn">理应送达时间</span>
            <span class="label-pt">Horário previsto de entrega</span>
          </label>
          <el-date-picker
            v-model="timeValue"
            :clearable="true"
            :default-time="['00:00:00', '23:59:59']"
            :format="$i18n.locale == 'zh' ? zhDate : ptDate"
            :placeholder="$t('newOrder.SelectDatetime')"
            class="filter-select"
            range-separator="~"
            size="small"
            type="datetimerange"
            value-format="yyyy-MM-dd HH:mm:ss"
            @change="onTimeChange"
          />
        </div>
        <div class="filter">
          <label>
            <span class="label-cn">货物类型</span>
            <span class="label-pt">Tipo de mercadorias</span>
          </label>
          <el-select v-model="filters.cargoType" class="filter-select" size="small">
            <el-option :label="$t('GlobalSetObj.all')" :value="null" />
            <el-option :label="$t('WorkCentreTable.normal')" :value="0" />
            <el-option :label="$t('GlobalSetObj.big01')" :value="1" />
          </el-select>
        </div>
        <div class="filter-actions">
          <el-button size="small" type="primary" @click="fetchSlaDashboard">{{ $t('collectionCenter.search') }}
          </el-button>
        </div>
      </div>
    </section>

    <!-- KPI 卡片区域 -->
    <section class="kpi-grid">
      <div
        v-for="(item, idx) in kpiData" :key="idx" :class="item.type"
        class="kpi"
      >
        <div class="kpi-top">
          <div class="kpi-icon">{{ item.icon }}</div>
          <div class="kpi-value">{{ item.value }}</div>
        </div>
        <div class="kpi-label">
          <span class="kpi-cn">{{ item.labelCn }}</span>
          <span class="kpi-pt">{{ item.labelPt }}</span>
        </div>
        <small v-if="item.extra">{{ item.extra }}</small>
      </div>
    </section>

    <!-- 主体内容：表格 + 排名面板 -->
    <section class="content-grid">
      <!-- 左侧表格 -->
      <article class="card">
        <div class="card-head">
          <span class="card-title">SLA Diário | SLA时效看板</span>
          <span class="card-meta">按大区 / 州展开 Expandir por Região / Estado</span>
        </div>
        <div class="table-wrap">
          <el-table
            :cell-style="cellStyle"
            :data="tableData"
            :header-cell-style="headerCellStyle"
            :row-class-name="tableRowClassName"
            border
            size="mini"
            style="width: 100%"
          >
            <el-table-column min-width="180">
              <template slot="header">
                <span class="th-cn">大区 / 州</span>
                <span class="th-pt">REGIÃO / ESTADO</span>
              </template>
              <template slot-scope="{ row }">
                <span v-if="row.isGroup" class="group-cell">
                  <i
                    :class="{ collapsed: !row.expanded }"
                    class="toggle"
                    @click="toggleExpand(row)"
                  >{{ row.expanded ? '−' : '+' }}</i>
                  {{ row.name }}
                </span>
                <span v-else-if="row.isTotal" class="total-cell">{{ row.name }}</span>
                <span v-else class="state-cell">{{ row.name }}</span>
              </template>
            </el-table-column>
            <el-table-column align="right" min-width="110">
              <template slot="header">
                <span class="th-cn">应送达包裹量</span>
                <span class="th-pt">TOTAL DE PACOTES</span>
              </template>
              <template slot-scope="{ row }">
                {{ formatNumber(row.p) }}
              </template>
            </el-table-column>
            <el-table-column align="right" min-width="110">
              <template slot="header">
                <span class="th-cn">已签收包裹量</span>
                <span class="th-pt">TOTAL DE ENTREGA</span>
              </template>
              <template slot-scope="{ row }">
                {{ formatNumber(row.d) }}
              </template>
            </el-table-column>
            <el-table-column align="right" min-width="90">
              <template slot="header">
                <span class="th-cn">签收率</span>
                <span class="th-pt">TAXA DE ENTREGA</span>
              </template>
              <template slot-scope="{ row }">
                <span :class="rateClass(row.dr)">{{ formatPercentage(row.dr) }}</span>
              </template>
            </el-table-column>
            <el-table-column align="right" min-width="110">
              <template slot="header">
                <span class="th-cn">准时签收量</span>
                <span class="th-pt">NO PRAZO</span>
              </template>
              <template slot-scope="{ row }">
                {{ formatNumber(row.on) }}
              </template>
            </el-table-column>
            <el-table-column align="right" min-width="100">
              <template slot="header">
                <span class="th-cn">SLA 准时率</span>
                <span class="th-pt">TAXA SLA</span>
              </template>
              <template slot-scope="{ row }">
                <span :class="rateClass(row.sla)">{{ formatPercentage(row.sla) }}</span>
              </template>
            </el-table-column>
            <el-table-column align="right" min-width="100">
              <template slot="header">
                <span class="th-cn">超时效量</span>
                <span class="th-pt">FORA DO PRAZO</span>
              </template>
              <template slot-scope="{ row }">
                {{ formatNumber(row.late) }}
              </template>
            </el-table-column>
            <el-table-column align="right" min-width="90">
              <template slot="header">
                <span class="th-cn">未签收量</span>
                <span class="th-pt">BACKLOG</span>
              </template>
              <template slot-scope="{ row }">
                {{ formatNumber(row.b) }}
              </template>
            </el-table-column>
            <el-table-column align="right" min-width="100">
              <template slot="header">
                <span class="th-cn">积压率</span>
                <span class="th-pt">TAXA DE BACKLOG</span>
              </template>
              <template slot-scope="{ row }">
                <span :class="{ bad: backlogRateHigh(row.br) }">{{
                  formatPercentage(row.br)
                }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </article>

      <!-- 右侧排名面板 -->
      <aside class="side-stack">
        <article class="card">
          <div class="card-head">
            <span class="card-title">各大区签收率</span>
            <span class="card-meta">Taxa de assinatura por região</span>
          </div>
          <div class="ranking">
            <div v-for="(item, idx) in deliveryRank" :key="'dr-' + idx" class="rank-row">
              <div :title="item.name" class="rank-name">{{ item.name }}</div>
              <el-progress
                :color="getProgressColor(item.value, 'delivery')"
                :percentage="item.value"
                :show-text="false"
                :stroke-width="16"
                class="rank-progress"
              />
              <div class="rank-value">{{ formatPercentage(item.value) }}</div>
            </div>
            <div class="chart-note">{{ $t('customerManagements.higherIsBetter') }}</div>
          </div>
        </article>
        <article class="card">
          <div class="card-head">
            <span class="card-title">各大区准时签收率</span>
            <span class="card-meta">Taxa SLA no prazo por região</span>
          </div>
          <div class="ranking">
            <div v-for="(item, idx) in slaRank" :key="'sr-' + idx" class="rank-row">
              <div :title="item.name" class="rank-name">{{ item.name }}</div>
              <el-progress
                :color="getProgressColor(item.value, 'sla')"
                :percentage="item.value"
                :show-text="false"
                :stroke-width="16"
                class="rank-progress"
              />
              <div class="rank-value">{{ formatPercentage(item.value) }}</div>
            </div>
            <div class="chart-note">{{ $t('customerManagements.higherIsBetter') }}</div>
          </div>
        </article>
      </aside>
    </section>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { getSlaDashboardQuery } from '@/api/newExpressAPI';
import { apiCustomerPage, apimerchantName, getLastSyncTime } from '@/api/customer';

dayjs.extend(isoWeek);

export default {
  name: 'SLATimelinessDashboard',
  data() {
    return {
      zhDate: 'yyyy-MM-dd HH:mm:ss',
      ptDate: 'dd-MM-yyyy HH:mm:ss',
      updateTime: '',
      filters: {
        week: String(dayjs().isoWeek()),
        customerId: '',
        merchantCode: '',
        cargoType: null
      },
      // 原始大区数据（后续表格对接用，暂由接口返回）
      regions: [],
      merchantList: [],
      customerNameList: [],
      // KPI 汇总数据
      summary: {
        backlogRate: '',
        deliveredCount: 0,
        deliveryRate: '',
        onTimeCount: 0,
        onTimeRate: '',
        outOfTimeCount: 0,
        totalCount: 0,
        unDeliveredCount: 0
      },
      // 各大区签收率排名
      deliveryRateRanking: [],
      // 各大区准时签收率排名
      slaRateRanking: [],
      // 展开状态
      expandState: {},
      timeValue: [],
      // 时间口径：'week' 按周 | 'range' 按时间范围（二者互斥）
      timeMode: 'week'
    };
  },
  computed: {
    // 当前年份的周下拉选项（第 1 周 ~ 当年最大周数，52/53）
    weekOptions() {
      const year = dayjs().year();
      // 12 月 28 日永远落在当年的最后一周，用其 ISO 周数作为当年最大周数
      const maxWeek = dayjs(`${year}-12-28`).isoWeek();
      const options = [];
      for (let i = 1; i <= maxWeek; i += 1) {
        options.push({
          // 使用和 pleaseChoose 一样的调用方式
          label: this.$t('customerManagements.weekLabel', { week: i }),
          value: String(i)
        });
      }
      return options;
    },
    // 将原始 regions 展开为表格行
    tableData() {
      const rows = [];
      this.regions.forEach((region, idx) => {
        const key = `region-${idx}`;
        const expanded = this.expandState[key] !== false; // 默认展开
        rows.push({
          _key: key,
          name: region.name,
          p: region.totalCount,
          d: region.deliveredCount,
          dr: region.deliveryRate,
          on: region.onTimeCount,
          sla: region.onTimeRate,
          late: region.outOfTimeCount,
          b: region.unDeliveredCount,
          br: region.backlogRate,
          isGroup: true,
          expanded
        });
        if (expanded) {
          (region.states || []).forEach((state, sIdx) => {
            rows.push({
              _key: `${key}-state-${sIdx}`,
              name: state.name,
              p: state.totalCount,
              d: state.deliveredCount,
              dr: state.deliveryRate,
              on: state.onTimeCount,
              sla: state.onTimeRate,
              late: state.outOfTimeCount,
              b: state.unDeliveredCount,
              br: state.backlogRate,
              isState: true
            });
          });
        }
      });
      // 合计行（由 summary 总量动态生成）
      const s = this.summary || {};
      rows.push({
        _key: 'total',
        name: '合计 Total',
        p: s.totalCount,
        d: s.deliveredCount,
        dr: s.deliveryRate,
        on: s.onTimeCount,
        sla: s.onTimeRate,
        late: s.outOfTimeCount,
        b: s.unDeliveredCount,
        br: s.backlogRate,
        isTotal: true
      });
      return rows;
    },
    // KPI 卡片数据（由 summary 动态生成）
    kpiData() {
      const s = this.summary || {};
      return [
        {
          icon: '▦',
          value: this.formatCompactNumber(s.totalCount),
          labelCn: '应送达包裹量',
          labelPt: 'Total de pacotes',
          type: ''
        },
        {
          icon: '✓',
          value: this.formatCompactNumber(s.deliveredCount),
          labelCn: '已签收包裹量',
          labelPt: 'Total de entrega',
          type: ''
        },
        {
          icon: '◇',
          value: this.formatPercentage(s.deliveryRate),
          labelCn: '签收率',
          labelPt: 'Taxa de entrega',
          type: ''
        },
        {
          icon: '◷',
          value: this.formatCompactNumber(s.onTimeCount),
          labelCn: '准时签收量',
          labelPt: 'No prazo',
          type: ''
        },
        {
          icon: '◴',
          value: this.formatPercentage(s.onTimeRate),
          labelCn: 'SLA 准时签收率',
          labelPt: 'Taxa SLA',
          type: ''
        },
        {
          icon: '!',
          value: this.formatCompactNumber(s.outOfTimeCount),
          labelCn: '超时效量',
          labelPt: 'Fora do prazo',
          type: 'negative'
        },
        {
          icon: '▤',
          value: this.formatCompactNumber(s.unDeliveredCount),
          labelCn: '未签收量',
          labelPt: 'Backlog',
          type: 'risk',
          extra: this.formatPercentage(s.backlogRate)
        }
      ];
    },
    // 各大区签收率排名（映射为 [name, number]）
    deliveryRank() {
      return (this.deliveryRateRanking || []).map((it) => ({
        name: it.regionName,
        value: this.parseRate(it.rate)
      }));
    },
    // 各大区 SLA 准时率排名（映射为 [name, number]）
    slaRank() {
      return (this.slaRateRanking || []).map((it) => ({
        name: it.regionName,
        value: this.parseRate(it.rate)
      }));
    }
  },
  created() {
    // 先初始化时间值，再查询数据
    // this.getTimer();
    // 确保初始 timeMode 为 'week'，避免 timeValue 为空时进入 range 分支
    this.timeMode = 'week';
    this.getLastSyncTimeData();
    this.fetchSlaDashboard();
    this.getCustomerPage();
    this.getMerchantList();
  },
  methods: {
    // 格式化数字（千位分隔符）
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '0';
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(num)) return '0';

      if (this.$i18n.locale === 'zh') {
        // 中文格式：1,234,567
        return num.toLocaleString('zh-CN');
      } else {
        // 葡语格式：1.234.567
        return num.toLocaleString('pt-BR');
      }
    },

    // 格式化百分比
    formatPercentage(value) {
      if (!value) return '0%';

      // 如果已经是百分比字符串
      if (typeof value === 'string' && value.includes('%')) {
        const num = parseFloat(value);
        if (isNaN(num)) return '0%';

        if (this.$i18n.locale === 'zh') {
          // 中文格式：65.74%
          return num.toFixed(2) + '%';
        } else {
          // 葡语格式：65,74%
          return num.toFixed(2).replace('.', ',') + '%';
        }
      }

      // 如果是数字
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(num)) return '0%';

      if (this.$i18n.locale === 'zh') {
        return num.toFixed(2) + '%';
      } else {
        return num.toFixed(2).replace('.', ',') + '%';
      }
    },
    // 格式化紧凑数字（K/M）
    formatCompactNumber(value) {
      if (value === null || value === undefined || value === '') return '0';
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(num)) return '0';

      const isZh = this.$i18n.locale === 'zh';

      // 百万级别
      if (Math.abs(num) >= 1000000) {
        const result = (num / 1000000).toFixed(1);
        return isZh ? result + 'M' : result.replace('.', ',') + 'M';
      }

      // 千级别
      if (Math.abs(num) >= 1000) {
        const result = (num / 1000).toFixed(1);
        return isZh ? result + 'K' : result.replace('.', ',') + 'K';
      }

      // 小于千的直接显示
      return num.toString();
    },

    // 获取运单时效的更新时间
    getLastSyncTimeData() {
      getLastSyncTime().then((res) => {
        if (res.code === 200) {
          this.updateTime = res.data;
        }
      });
    },
    // 进入页面查询 SLA 时效看板数据
    fetchSlaDashboard() {
      const params = {
        customerId: this.filters.customerId === 'all' ? '' : this.filters.customerId,
        merchantCode: this.filters.merchantCode === 'all' ? '' : this.filters.merchantCode,
        cargoType: this.filters.cargoType
      };

      if (this.timeMode === 'range') {
        // 时间范围口径 - 添加空值判断
        if (this.timeValue && this.timeValue.length === 2) {
          params.startTime = this.timeValue[0];
          params.endTime = this.timeValue[1];
        } else {
          // 如果时间范围为空，提示用户
          this.$message.warning(this.$t('mdfeTranslate.selectExpectedDeliveryDate'));
          return;
        }
      } else {
        // 按周口径（默认）- 添加周次判断
        if (!this.filters.week) {
          this.$message.warning(this.$t('mdfeTranslate.selectDeliveryWeek'));
          return;
        }
        params.deliveryYear = dayjs().year();
        params.deliveryWeek = Number(this.filters.week);
      }

      getSlaDashboardQuery(params)
        .then((res) => {
          console.log('SLA时效看板数据：', res);
          const data = res?.data || {};
          // const data = dataList;
          // KPI 卡片
          if (data.summary) {
            this.summary = data.summary;
          }
          // 各大区签收率排名
          if (data.deliveryRateRanking) {
            this.deliveryRateRanking = data.deliveryRateRanking;
          }
          // 各大区准时签收率排名
          if (data.slaRateRanking) {
            this.slaRateRanking = data.slaRateRanking;
          }
          // SLA 时效看板主体表格（各大区/州明细）
          if (data.regions) {
            this.regions = data.regions;
          }
          this.getLastSyncTimeData();
        })
        .catch((error) => {
          console.error('查询SLA时效看板数据失败：', error);
          this.$message.error('查询失败，请稍后重试');
        });
    },
    // 获取最近一星期时间
    getTimer() {
      const day = new Date();
      const brazilTime = day.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' });
      const today = dayjs(brazilTime).format('YYYY-MM-DD');
      // const oneWeekAgo = dayjs(brazilTime).subtract(1, 'week').format('YYYY-MM-DD');
      this.timeValue = [today + ' 00:00:00', today + ' 23:59:59'];
    },
    // 选择「应交付周」：清空时间范围，锁定周口径
    onWeekChange(val) {
      if (val) {
        this.timeMode = 'week';
        // 清空时间范围选择器
        this.timeValue = [];
      } else {
        // 清空周选择时，切到时间范围口径
        this.timeMode = 'range';
      }
    },
    // 选择「理应送达时间」：清空周选择，锁定时间口径
    onTimeChange(val) {
      if (val && val.length === 2) {
        this.timeMode = 'range';
        // 清空周选择器
        this.filters.week = '';
      } else {
        // 清空时间范围时，切回周口径
        this.timeMode = 'week';
        // 切回周口径时若周为空，回填当前周
        if (!this.filters.week) {
          this.filters.week = String(dayjs().isoWeek());
        }
      }
    },
    // 将百分比字符串转为数字（如 "17.74%" -> 17.74）
    parseRate(val) {
      return parseFloat(val) || 0;
    },
    // 获取进度条颜色
    getProgressColor(value, type) {
      if (type === 'delivery') {
        // 签收率：>=95 绿色，>=90 黄绿色，>=85 橙色，<85 红色
        if (value >= 95) return '#43f19b';
        if (value >= 90) return '#8ee06a';
        if (value >= 85) return '#ffbd59';
        return '#ff6d7b';
      } else {
        // SLA准时率：>=90 绿色，>=80 黄绿色，>=70 橙色，<70 红色
        if (value >= 90) return '#43f19b';
        if (value >= 80) return '#8ee06a';
        if (value >= 70) return '#ffbd59';
        return '#ff6d7b';
      }
    },
    // 表格行样式
    tableRowClassName({ row }) {
      if (row.isGroup) return 'group-row';
      if (row.isTotal) return 'total-row';
      if (row.isState) return 'state-row';
      return '';
    },
    // 表头样式
    headerCellStyle() {
      return {
        background: '#0d1e21',
        color: '#98aca5',
        borderBottom: '1px solid rgba(25,227,125,.20)',
        fontWeight: 700,
        padding: '10px 9px',
        fontSize: '12px'
      };
    },
    // 单元格样式
    cellStyle() {
      return {
        borderBottom: '1px solid rgba(255,255,255,.045)',
        color: '#b1c2bc',
        padding: '7px 9px',
        fontSize: '12px'
      };
    },
    // 切换展开/收起
    toggleExpand(row) {
      this.expandState = {
        ...this.expandState,
        [row._key]: !row.expanded
      };
    },
    // 比率颜色分类
    rateClass(val) {
      if (!val) return '';
      const n = parseFloat(val);
      if (n >= 95) return 'good'; // 绿色
      if (n >= 90) return 'warn'; // 黄色
      if (n >= 85) return 'orange'; // 橙色 (需新增)
      return 'bad'; // 红色
    },
    // 积压率是否高（>3%）
    backlogRateHigh(val) {
      return parseFloat(val) > 3;
    },
    getCustomerPage(name = '') {
      const param = {
        pageSize: 10,
        pageNumber: 1,
        params: {
          quickSearch: ''
        }
      };
      param.params.quickSearch = name;
      apiCustomerPage(param, false).then((res) => {
        if (res.code === 200) {
          this.customerNameList = res.data.records;
        }
      });
    },
    // 获取商户列表
    getMerchantList(val = '') {
      const data = {
        pageSize: 20,
        pageNumber: 1,
        params: {
          merchantName: ''
        }
      };
      data.params.merchantName = val;
      apimerchantName(data, false).then((res) => {
        if (res.code === 200) {
          this.merchantList = res.data.records;
        }
      });
    }
  }
};
</script>

<style scoped>
/* ===== 暗色仪表盘主题 ===== */
.sla-dashboard {
  position: relative;
  min-width: 1240px;
  min-height: 100%;
  padding: 20px;
  border-radius: 18px;
  overflow: hidden;
  color: #eaf7f2;
  background: radial-gradient(circle at 82% -8%, rgba(25, 227, 125, 0.18), transparent 30%),
    radial-gradient(circle at 5% 100%, rgba(0, 153, 255, 0.1), transparent 34%),
    linear-gradient(145deg, #0a1417 0%, #0d1a1d 55%, #081113 100%);
  border: 1px solid rgba(25, 227, 125, 0.16);
  box-shadow: 0 18px 50px rgba(16, 24, 40, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.sla-dashboard::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.18;
  background-image: linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
  background-size: 28px 28px;
}

.sla-dashboard > * {
  position: relative;
  z-index: 1;
}

/* ===== 页面头部 ===== */
.page-head {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 24px;
  margin-bottom: 14px;
  padding: 2px 2px 0;
}

.title-wrap {
  display: flex;
  align-items: center;
  gap: 14px;
}

.page-icon {
  width: 34px;
  height: 34px;
  background: linear-gradient(135deg, rgba(25, 227, 125, 0.22), rgba(0, 159, 255, 0.08));
  color: #38f095;
  border: 1px solid rgba(25, 227, 125, 0.25);
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 18px;
  box-shadow: 0 0 28px rgba(25, 227, 125, 0.12);
}

.page-title {
  font-size: 18px;
  margin: 0;
  line-height: 1.2;
  font-weight: 750;
  letter-spacing: -0.3px;
  background: linear-gradient(90deg, #f5fffb 0%, #75ffc2 72%, #62caff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 0 24px rgba(25, 227, 125, 0.08);
}

.update {
  margin-left: 8px;
  padding-left: 18px;
  border-left: 1px solid rgba(143, 180, 167, 0.18);
  font-size: 12px;
  color: #70857e;
  line-height: 1.5;
}

.update b {
  display: block;
  color: #c4d7d0;
  font-size: 13px;
}

/* ===== 筛选项 ===== */
.filters {
  display: flex;
  gap: 10px;
}

.filter {
  width: 168px;
}

.filter--date {
  width: 350px;
}

.filter label {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: #8ea29b;
  font-size: 12px;
  margin: 0 0 6px 3px;
  line-height: 1.25;
  min-height: 31px;
}

.label-cn {
  font-size: 14px;
  color: #a9bbb5;
  font-weight: 700;
}

.label-pt {
  font-size: 12px;
  color: #647b73;
  margin-top: 2px;
  font-weight: 500;
}

.filter-select {
  width: 100%;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
}

/* ===== KPI 卡片 ===== */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.kpi {
  background: linear-gradient(145deg, rgba(18, 31, 33, 0.96), rgba(10, 21, 23, 0.98));
  border: 1px solid rgba(25, 227, 125, 0.16);
  border-radius: 12px;
  padding: 13px 14px;
  min-height: 100px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.035);
  position: relative;
  overflow: hidden;
  transition: 0.25s ease;
}

.kpi:hover {
  transform: translateY(-3px);
  border-color: rgba(25, 227, 125, 0.42);
  box-shadow: 0 18px 38px rgba(0, 0, 0, 0.32), 0 0 24px rgba(25, 227, 125, 0.1);
}

.kpi::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, #31f49a, #0aa95a);
  box-shadow: 0 0 14px rgba(25, 227, 125, 0.6);
}

.kpi::after {
  content: '';
  position: absolute;
  right: -25px;
  top: -33px;
  width: 108px;
  height: 108px;
  background: radial-gradient(circle, rgba(25, 227, 125, 0.16), transparent 65%);
}

.kpi-top {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 1;
}

.kpi-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(25, 227, 125, 0.2), rgba(25, 227, 125, 0.05));
  color: #3df497;
  border: 1px solid rgba(25, 227, 125, 0.18);
  display: grid;
  place-items: center;
  font-size: 17px;
  box-shadow: 0 0 20px rgba(25, 227, 125, 0.08);
}

.kpi-value {
  font-size: 21px;
  font-weight: 800;
  line-height: 1.1;
  color: #f0fff9;
  text-shadow: 0 0 18px rgba(25, 227, 125, 0.11);
}

.kpi-label {
  margin-top: 9px;
  color: #8ba099;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  line-height: 1.2;
}

.kpi-cn {
  font-size: 14px;
  font-weight: 650;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi-pt {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.72;
}

.kpi small {
  position: absolute;
  right: 12px;
  bottom: 12px;
  color: #9aafa7;
  font-size: 12px;
}

/* KPI 负面/警告变体 */
.kpi.negative {
  background: linear-gradient(145deg, rgba(31, 28, 20, 0.98), rgba(17, 23, 24, 0.98));
  border-color: rgba(255, 184, 77, 0.26);
}

.kpi.negative::before {
  background: linear-gradient(180deg, #ffc45e, #d98210);
  box-shadow: 0 0 10px rgba(255, 166, 41, 0.38);
}

.kpi.negative .kpi-icon {
  background: rgba(255, 184, 77, 0.1);
  color: #ffc66d;
  border-color: rgba(255, 184, 77, 0.22);
  box-shadow: none;
}

.kpi.negative .kpi-value {
  color: #ffe0a8;
  text-shadow: none;
}

.kpi.negative .kpi-label {
  color: #c9a96f;
}

.kpi.negative:hover {
  border-color: rgba(255, 184, 77, 0.38);
  box-shadow: 0 18px 38px rgba(0, 0, 0, 0.3), 0 0 18px rgba(255, 166, 41, 0.06);
}

.kpi.risk {
  background: linear-gradient(145deg, rgba(34, 24, 27, 0.98), rgba(17, 22, 24, 0.98));
  border-color: rgba(255, 92, 108, 0.28);
}

.kpi.risk::before {
  background: linear-gradient(180deg, #ff7c89, #d83d50);
  box-shadow: 0 0 10px rgba(255, 72, 92, 0.38);
}

.kpi.risk .kpi-icon {
  background: rgba(255, 92, 108, 0.1);
  color: #ff8793;
  border-color: rgba(255, 92, 108, 0.22);
  box-shadow: none;
}

.kpi.risk .kpi-value {
  color: #ffb7be;
  text-shadow: none;
}

.kpi.risk .kpi-label {
  color: #cf9299;
}

.kpi.risk:hover {
  border-color: rgba(255, 92, 108, 0.4);
  box-shadow: 0 18px 38px rgba(0, 0, 0, 0.3), 0 0 18px rgba(255, 72, 92, 0.07);
}

/* ===== 内容网格 ===== */
.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 2.55fr) minmax(360px, 1fr);
  gap: 14px;
  align-items: start;
}

/* ===== 卡片 ===== */
.card {
  background: linear-gradient(155deg, rgba(17, 29, 31, 0.96), rgba(9, 18, 20, 0.98));
  border: 1px solid rgba(143, 180, 167, 0.16);
  border-radius: 12px;
  box-shadow: 0 16px 42px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.card-head {
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 18px;
  border-bottom: 1px solid rgba(143, 180, 167, 0.16);
  gap: 10px;
  background: linear-gradient(90deg, rgba(25, 227, 125, 0.075), transparent 42%);
}

.card-title {
  font-size: 15px;
  font-weight: 750;
  color: #eaf7f2;
}

.card-meta {
  font-size: 12px;
  color: #758a82;
}

/* ===== 表格 ===== */
.table-wrap {
  padding: 0 14px 14px;
}

/* 表头双语 */
.th-cn {
  display: block;
  font-size: 12px;
  line-height: 1.2;
  color: #b8cbc4;
  font-weight: 750;
  white-space: normal;
}

.th-pt {
  display: block;
  font-size: 12px;
  line-height: 1.15;
  color: #7f958d;
  font-weight: 600;
  letter-spacing: 0.1px;
  text-transform: uppercase;
  margin-top: 3px;
  white-space: normal;
}

/* 表格行样式 */
.group-cell {
  display: flex;
  align-items: center;
  gap: 7px;
}

.toggle {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(135deg, #22e889, #07974e);
  box-shadow: 0 0 14px rgba(25, 227, 125, 0.22);
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-style: normal;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle.collapsed {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}

.state-cell {
  padding-left: 32px !important;
  color: #81958e;
}

.total-cell {
  font-weight: 800;
  color: #dff9ed;
}

/* 颜色标记 */
.good {
  color: #43f19b;
  font-weight: 700;
}

.warn {
  color: #ffbd59;
  font-weight: 700;
}

.orange {
  color: #ff9a3d;
  font-weight: 700;
}

.bad {
  color: #ff6d7b;
  font-weight: 700;
}

/* ===== 右侧排名 ===== */
.side-stack {
  display: grid;
  gap: 14px;
}

.ranking {
  padding: 8px 18px 18px;
}

.rank-row {
  display: grid;
  grid-template-columns: 150px 1fr 60px;
  gap: 12px;
  align-items: center;
  margin: 16px 0;
}

.rank-name {
  text-align: right;
  font-size: 12px;
  color: #9fb2ab;
  white-space: normal;
  word-break: break-all;
  line-height: 1.3;
}

.rank-progress {
  flex: 1;
}

.rank-progress /deep/ .el-progress-bar__outer {
  background: #081214 !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.35);
}

.rank-progress /deep/ .el-progress-bar__inner {
  border-radius: 8px;
  box-shadow: 0 0 16px rgba(25, 227, 125, 0.28);
  transition: width 0.6s ease;
}

.rank-value {
  text-align: right;
  font-size: 12px;
  font-weight: 800;
  color: #dff7ed;
}

.chart-note {
  font-size: 12px;
  color: #667a73;
  margin: 4px 0 0;
  text-align: right;
}

/* ===== 覆盖 Element UI 表格样式 ===== */
.sla-dashboard /deep/ .el-table,
.sla-dashboard /deep/ .el-table__inner-wrapper,
.sla-dashboard /deep/ .el-table__header-wrapper,
.sla-dashboard /deep/ .el-table__body-wrapper,
.sla-dashboard /deep/ .el-table__footer-wrapper {
  background: transparent !important;
  color: #b1c2bc;
  font-size: 12px;
  border: none;
}

.sla-dashboard /deep/ .el-table::before {
  display: none;
}

.sla-dashboard /deep/ .el-table th,
.sla-dashboard /deep/ .el-table tr {
  background: transparent !important;
}

/* 表头单元格 */
.sla-dashboard /deep/ .el-table__header-wrapper th.el-table__cell,
.sla-dashboard /deep/ .el-table__fixed-header-wrapper th.el-table__cell {
  background-color: #0d181a !important;
  color: #98aca5;
  border-bottom-color: rgba(25, 227, 125, 0.2);
}

/* 数据单元格 */
.sla-dashboard /deep/ .el-table td.el-table__cell {
  background-color: #0d181a !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.045);
}

/* 斑马纹覆盖 */
.sla-dashboard /deep/ .el-table__row--striped td.el-table__cell {
  background-color: #0d181a !important;
}

/* selected / current-row 覆盖 */
.sla-dashboard /deep/ .el-table__body tr.current-row > td.el-table__cell,
.sla-dashboard /deep/ .el-table__body tr.el-table__row--selected > td.el-table__cell,
.sla-dashboard /deep/ .el-table__body tr.is-selected > td.el-table__cell {
  background-color: rgba(25, 227, 125, 0.12) !important;
}

/* 大区行 */
/deep/ .group-row td {
  background: linear-gradient(
    90deg,
    rgba(25, 227, 125, 0.15),
    rgba(25, 227, 125, 0.055)
  ) !important;
  color: #67f8af;
  border-bottom-color: rgba(25, 227, 125, 0.13);
  font-weight: 800;
}

/* 州行 */
/deep/ .state-row td:first-child {
  padding-left: 32px;
  color: #81958e;
}

/* 合计行 */
/deep/ .total-row td {
  background: rgba(25, 227, 125, 0.095) !important;
  color: #dff9ed;
  font-weight: 800;
  border-top: 1px solid rgba(25, 227, 125, 0.28);
}

/* hover 效果 */
/deep/ .el-table__body tr:hover > td {
  background: rgba(25, 227, 125, 0.035) !important;
}

/deep/ .group-row:hover > td {
  background: linear-gradient(90deg, rgba(25, 227, 125, 0.2), rgba(25, 227, 125, 0.08)) !important;
}

/deep/ .total-row:hover > td {
  background: rgba(25, 227, 125, 0.13) !important;
}

/* Select 下拉框暗色主题 */
/deep/ .el-select .el-input__inner {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.025));
  border-color: rgba(143, 180, 167, 0.23);
  color: #d8e6e1;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035), 0 8px 24px rgba(0, 0, 0, 0.16);
}

/deep/ .el-select .el-input__inner:hover {
  border-color: rgba(25, 227, 125, 0.35);
}

/deep/ .el-select .el-input__suffix .el-input__suffix-inner .el-select__caret {
  color: #91a59e;
}

/* el-date-picker 暗色主题 */
.sla-dashboard /deep/ .el-date-editor.el-input,
.sla-dashboard /deep/ .el-date-editor.el-input__inner,
.sla-dashboard /deep/ .el-range-editor.el-input__inner {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.025));
  border-color: rgba(143, 180, 167, 0.23);
  color: #d8e6e1;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035), 0 8px 24px rgba(0, 0, 0, 0.16);
}

.sla-dashboard /deep/ .el-date-editor.el-input:hover,
.sla-dashboard /deep/ .el-date-editor.el-input__inner:hover,
.sla-dashboard /deep/ .el-range-editor.el-input__inner:hover {
  border-color: rgba(25, 227, 125, 0.35);
}

.sla-dashboard /deep/ .el-range-editor .el-range-input {
  background: transparent !important;
  color: #d8e6e1 !important;
  text-align: center;
}

.sla-dashboard /deep/ .el-range-editor .el-range-separator {
  color: #91a59e;
  line-height: 32px;
}

.sla-dashboard /deep/ .el-date-editor .el-input__icon,
.sla-dashboard /deep/ .el-date-editor .el-input__suffix .el-input__icon,
.sla-dashboard /deep/ .el-date-editor .el-input__prefix .el-input__icon {
  color: #91a59e;
}

.sla-dashboard /deep/ .el-date-editor .el-input__suffix .el-input__suffix-inner {
  color: #91a59e;
}

/* 响应式 */
@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .sla-dashboard {
    min-width: 1100px;
    min-height: 100%;
  }

  .content-grid {
    grid-template-columns: 1fr 360px;
  }
}
</style>
